<template>
  <div>
    <!-- User info (if applicable) -->
    <div v-if="$route.query.userId" class="d-flex gap-1 mb-2">
      <span class="d-flex align-items-center cursor-pointer" @click="resetOrders"><em class="fa fa-times"></em></span>
      <span>{{$t("ordersFor", $route.query.userEmail, {email: $route.query.userEmail})}}</span>
    </div>
    <!-- Status filter -->
    <div class="actions row col-lg-12 mb-2 p-0 m-0">
      <span class="col-lg-6 m-0 p-0"> 
        <button v-bind:class="{'font-weight-bold': statusGroup == ACTIVE}" class="status-button" @click="statusGroup = ACTIVE">{{ $t('active') }}</button> | <button v-bind:class="{'font-weight-bold': statusGroup == ARCHIVE}" class="status-button" href="#" @click="statusGroup = ARCHIVE">{{ $t('archive') }}</button>
      </span>
      <span class="col-lg-6 m-0 p-0"> 
        <OptionsDropdown v-if="statusGroup == ACTIVE" v-model="statuses" :options="activeStatusOptions" :title="$t('status')" :index="0"></OptionsDropdown>
        <OptionsDropdown v-else-if="statusGroup == ARCHIVE" v-model="statuses" :options="archiveStatusOptions" :title="$t('status')" :index="0"></OptionsDropdown>
      </span>
    </div>
    <!-- Multi edit -->
    <div class="d-flex my-4 gap-1 align-items-center" v-if="selectedOrders.length">
      <span class="d-flex align-items-center cursor-pointer" @click="unselectedOrdersClicked" ><em class="fa fa-times"></em></span>
      <span class="flex-grow-1">{{$t('ordersSelected', selectedOrders.length, {count: selectedOrders.length})}}</span>
      <button class="btn btn-secondary" type="button" @click="editStatus = true">{{$t('changeStatus')}}</button>
      <button class="btn btn-secondary" type="button" @click="editDelivery = true">{{$t('changeDelivery')}}</button>
    </div>
    <!-- Search bar -->
    <SearchBar @search-clicked="searchClicked"></SearchBar>
    <!-- Table -->
    <CTableWrapper ref="table" hover striped :caption="caption" :items="items" :actions="actions" :pages="pages" :fields="fields" clickable-rows @row-clicked="rowClicked" @action-clicked="actionClicked" @update-requested="updateItemsRequested" :sorter="{external: true}" :sorterValue="sorterValue" @sorter-value-changed="sorterValueChanged" :loading="loading">
    </CTableWrapper>
    <!-- Pop ups -->
    <OrderPopUp :order="selectedViewOrder" @closeConfirmRequested="selectedViewOrder = null" v-model="selectedViewOrder" @update-order="orderUpdateRequested"></OrderPopUp>
    <StatusPopUp :orders="selectedOrders" @closeConfirmRequested="onOrdersChanged" v-model="editStatus"></StatusPopUp>
    <DeliveryPopUp :orders="selectedOrders" @closeConfirmRequested="onOrdersChanged" v-model="editDelivery"></DeliveryPopUp>
  </div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from '@/controls/PaginatedTable.vue'
import SearchBar from '@/controls/SearchBar.vue'
import router from '@/router';
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToCamelCase, convertToPascalCase, formatDateTime, formatDate } from "@/helpers/utils.js";
import OrderPopUp from "@/controls/popups/OrderPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import StatusPopUp from "@/controls/popups/StatusPopUp.vue";
import DeliveryPopUp from "@/controls/popups/DeliveryPopUp.vue";

const ACTIVE = "active";
const ARCHIVE = "archive";

export default {
  components: { CTableWrapper, SearchBar, OrderPopUp, OptionsDropdown, StatusPopUp, DeliveryPopUp },
  data () {
    return {
      selectedViewOrder: null,
      selectedOrders: [],
      searchText: '',
      caption: i18n.t('orders'),
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      fields: [
        { key: 'id', label: i18n.t('id'), _classes: 'font-weight-bold', sortable: true },
        { key: 'status', label: i18n.t('status'),  sortable: true },
        { key: 'fullName', label: i18n.t('name'),  sortable: true },
      ],
      actions: [
        { key: 'view', label: i18n.t('view') },
        { key: 'remove', label: i18n.t('remove') }
      ],
      statusGroup: null,
      statuses: [],
      activeStatusOptions: [{label: this.$t("orderStatus.pendingPayment"), key: "PendingPayment"}, {label: this.$t("orderStatus.processing"), key: "Processing"}, {label: this.$t("orderStatus.sent"), key: "Sent"} ],
      archiveStatusOptions: [ {label: this.$t("orderStatus.concluded"), key: "Concluded"}, {label: this.$t("orderStatus.canceled"), key: "Cancelled"} ],
      sorterValue: { column: 'id', asc: false },
      loading: false,
      editStatus: false,
      editDelivery: false,
      //
      ACTIVE: ACTIVE,
      ARCHIVE: ARCHIVE
    }
  },
  computed: {
    ...mapState('orders', ['getOrdersOutput']),
    ...mapState('orders', ['getOrderByIdOutput']),
    ...mapState('orders', ['deleteOrderByIdOutput']),
    orderInfo: function() {
        return this.getOrdersOutput ? this.getOrdersOutput : null;
    },
    items: function()
    {
      var result = [];

      if(this.orderInfo && this.orderInfo.data)
      {
        result = this.orderInfo.data;
        result.forEach(order => 
        { 
          // Show color according to status
          order["_classes"] = this.getStatusClass(order.status);
          order['status'] = this.$t(`orderStatus.${convertToCamelCase(order.status)}`);
        });
      }

      return result;
    },
    pages: function()
    {
      return this.orderInfo ? this.orderInfo.totalPages : 1;
    }
  },
  mounted: function()
  {
    this.statusGroup = ACTIVE;
  },
  methods: {
    ...mapActions('orders', ['getOrders']),
    ...mapActions('orders', ['getOrderById']),
    ...mapActions('orders', ['deleteOrderById']),
    rowClicked (item) {
      // this.actionClicked(item, {key: 'view'});
      const exists = this.selectedOrders.includes(item)

      if (exists) {
        this.selectedOrders = this.selectedOrders.filter((c) => { return c !== item })
        item["_classes"] = this.getStatusClass(item.status);
      } else {
        this.selectedOrders.push(item);
        item["_classes"] = "table-primary";
      }
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'view':
        {
          this.loading = true;
          this.getOrderById(item.id);
          break;
        }
        case 'remove':
        {
          if(confirm(this.$i18n.tc('confirmOrderRemove', item.id, { id: item.id }))){
            this.deleteOrderById(item.id);
          }
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      //Make request
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var input = new InputGetList(itemsPerPage, currentPage, this.searchText, this.sorterValue.asc ? 1 : 2, convertToPascalCase(this.sorterValue.column));

      // List specific parameters
      input.statuses = this.statuses;
      if(this.$route.query.userId)
      {
        input.userId = this.$route.query.userId;
      }
      
      this.getOrders(input); 
    },
    addOrder() 
    {
      router.push({ name: 'OrderAdd', params: { type: this.$route.params.type }});
    },
    searchClicked(searchText)
    {
      this.searchText = searchText;
      this.$refs.table.reset();

      this.updateItemsRequested(1, this.itemsPerPage);
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    },
    orderUpdateRequested(id)
    {
      this.updateItemsRequested(1, this.itemsPerPage);
      this.getOrderById(id);
    },
    getStatusClass(status)
    {
      switch(status)
      {
        case "PendingPayment":
          {
            return "table-secondary";
        } 
        case "Processing":
          {
            return "table-warning";
        } 
        case "Sent":
          {
            return "table-light";
        } 
        case "Concluded":
          {
            return "table-success";
        }
        case "Cancelled":
          {
            return "table-danger";
        }
      }
    },
    unselectedOrdersClicked: function()
    {
      this.selectedOrders = [];
      this.items.forEach(order => 
        { 
          order["_classes"] = this.getStatusClass(order.status);
        });
    },
    onOrdersChanged: function(update)
    {
      if(update)
      {
        this.editStatus = false;
        this.editDelivery = false;

        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    resetOrders: function()
    {
      router.push({name: "Orders"});
    }
  },
  watch:
  {
    getOrderByIdOutput: function (output) {
      this.loading = output === null;

      if (!output) {
        alert(this.$i18n.t("errorMsg"));
      }
      else {
        this.selectedViewOrder = output;
      }
    },
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true // Watch for changes on the properties
    },
    deleteOrderByIdOutput: function(val)
    {
      this.loading = true;

      if(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    getOrdersOutput: function(val)
     {
      if(val != null)
      {
        this.loading = false;
      }
    },
    statusGroup: function(val)
    {
      if(val == ACTIVE)
      {
        this.statuses = ["PendingPayment", "Processing", "Sent"];
      }
      else if(val == ARCHIVE)
      {
        this.statuses = ["Concluded", "Cancelled"];
      }
    },
    statuses: function(val, oldVal)
    {
      if(val.length !== oldVal.length || !val.every(val => oldVal.includes(val))) { // This checks if the arrays have different values
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    $route(to) {
      if (to.name == "OrdersList") {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
  }
}
</script>